import { useState } from "react";
import { ArrowRight, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

const Label = ({ children }) => (
  <label className="block text-xs uppercase tracking-[0.15em] font-bold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--text-secondary)' }}>{children}</label>
);
const Input = (props) => <input className="brutalist-input" {...props} />;
const SectionTitle = ({ children }) => (
  <h3 className="text-sm font-bold text-[#00A3E0] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</h3>
);

export default function ThreeCircuitStep1({ formData, updateForm, onNext }) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const set = (field) => (e) => updateForm({ [field]: e.target.value });

  const handleNext = () => {
    if (!formData.client_name.trim()) { toast.error("Please enter the client name"); return; }
    if (!formData.description_of_works.trim()) { toast.error("Please describe the works"); return; }
    onNext();
  };

  return (
    <div className="form-container max-w-3xl mx-auto" data-testid="tcmw-step-1">
      <div className="border-b-2 pb-5 mb-5" style={{ borderColor: 'var(--border-light)' }}>
        <SectionTitle>Part 1: Details of the Minor Works</SectionTitle>
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="form-field"><Label>Client name</Label><Input value={formData.client_name} onChange={set("client_name")} placeholder="Client name" /></div>
            <div className="form-field"><Label>Client email (optional)</Label><Input type="email" value={formData.client_email} onChange={set("client_email")} /></div>
          </div>
          <div className="form-field"><Label>Description of minor works</Label><textarea className="brutalist-input min-h-[70px] resize-y" value={formData.description_of_works} onChange={set("description_of_works")} rows={2} /></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-3"><Label>Installation address</Label><Input value={formData.installation_address} onChange={set("installation_address")} /></div>
            <div className="form-field"><Label>Postcode</Label><Input value={formData.installation_postcode} onChange={set("installation_postcode")} /></div>
          </div>
          <div className="form-field"><Label>Departures from BS 7671</Label><Input value={formData.departures} onChange={set("departures")} placeholder="N/A" /></div>
          <div className="form-field"><Label>Comments on existing installation</Label><textarea className="brutalist-input min-h-[60px] resize-y" value={formData.comments_existing} onChange={set("comments_existing")} placeholder="N/A" /></div>
        </div>
      </div>

      <div className="border-b-2 pb-5 mb-5" style={{ borderColor: 'var(--border-light)' }}>
        <SectionTitle>Part 2: Earthing and Bonding</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="form-field">
            <Label>System earthing</Label>
            <select className="brutalist-input" value={formData.system_earthing} onChange={set("system_earthing")}>
              <option value="">Select...</option>
              <option value="TN-S">TN-S</option>
              <option value="TN-C-S">TN-C-S</option>
              <option value="TT">TT</option>
            </select>
          </div>
          <div className="form-field"><Label>Zdb (Ohm)</Label><Input value={formData.zdb} onChange={set("zdb")} /></div>
          <div className="form-field">
            <Label>Adequate main conductors</Label>
            <select className="brutalist-input" value={formData.adequate_main_conductors} onChange={set("adequate_main_conductors")}>
              <option value="">Select...</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
          {[{ f: "bonding_water", l: "Water" }, { f: "bonding_gas", l: "Gas" }, { f: "bonding_oil", l: "Oil" }, { f: "bonding_structural", l: "Structural" }].map((b) => (
            <div key={b.f}>
              <label className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{b.l}</label>
              <select className="brutalist-input mt-0.5" value={formData[b.f]} onChange={set(b.f)}>
                <option value="N/A">N/A</option><option value="Yes">Yes</option><option value="No">No</option>
              </select>
            </div>
          ))}
          <div className="form-field"><label className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Other</label><Input value={formData.bonding_other} onChange={set("bonding_other")} /></div>
          <div className="form-field"><label className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Earthing conductor</label><Input value={formData.earthing_conductor} onChange={set("earthing_conductor")} /></div>
        </div>
      </div>

      <div className="mb-5">
        <Label>Certificate date</Label>
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <button className="brutalist-input flex items-center justify-between cursor-pointer" type="button">
              <span style={{ color: formData.cert_date ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                {formData.cert_date ? format(formData.cert_date, "dd/MM/yyyy") : "Select date"}
              </span>
              <CalendarIcon className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 border-2 rounded-none" style={{ borderColor: 'var(--border-main)' }} align="start">
            <Calendar mode="single" selected={formData.cert_date} onSelect={(d) => { updateForm({ cert_date: d }); setCalendarOpen(false); }} />
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex justify-end">
        <button data-testid="tcmw-step-1-next" onClick={handleNext} className="brutalist-btn flex items-center gap-2">
          Next <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
